import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  deleteApiShipmentsByIdDelayMutation,
  postApiShipmentsByIdCancelMutation,
  postApiShipmentsByIdDelayMutation,
  postApiShipmentsByIdHandoverMutation,
  postApiShipmentsByIdHandoverRequestOutcomeMutation,
  postApiShipmentsByIdReturnToPlanMutation,
  postApiShipmentsByIdReturnToPreparationMutation,
  postApiShipmentsByIdSendToDeliveryMutation,
  postApiShipmentsByIdTakeIntoWorkMutation,
  postApiShipmentsFromForecastMutation,
  postApiShipmentsMutation,
  putApiShipmentsByIdApprovalStepsMutation,
  putApiShipmentsByIdDeliveryMutation,
  putApiShipmentsByIdDocumentsMutation,
  putApiShipmentsByIdMutation,
  putApiShipmentsByIdNextShipmentIntervalsMutation,
  type DeleteApiShipmentsByIdDelayData,
  type DeleteApiShipmentsByIdDelayError,
  type Options,
  type PostApiShipmentsByIdCancelData,
  type PostApiShipmentsByIdCancelError,
  type PostApiShipmentsByIdDelayData,
  type PostApiShipmentsByIdDelayError,
  type PostApiShipmentsByIdHandoverData,
  type PostApiShipmentsByIdHandoverError,
  type PostApiShipmentsByIdHandoverRequestOutcomeData,
  type PostApiShipmentsByIdHandoverRequestOutcomeError,
  type PostApiShipmentsByIdReturnToPlanData,
  type PostApiShipmentsByIdReturnToPlanError,
  type PostApiShipmentsByIdReturnToPreparationData,
  type PostApiShipmentsByIdReturnToPreparationError,
  type PostApiShipmentsByIdSendToDeliveryData,
  type PostApiShipmentsByIdSendToDeliveryError,
  type PostApiShipmentsByIdTakeIntoWorkData,
  type PostApiShipmentsByIdTakeIntoWorkError,
  type PostApiShipmentsData,
  type PostApiShipmentsError,
  type PostApiShipmentsFromForecastData,
  type PostApiShipmentsFromForecastError,
  type PutApiShipmentsByIdApprovalStepsData,
  type PutApiShipmentsByIdApprovalStepsError,
  type PutApiShipmentsByIdData,
  type PutApiShipmentsByIdDeliveryData,
  type PutApiShipmentsByIdDeliveryError,
  type PutApiShipmentsByIdDocumentsData,
  type PutApiShipmentsByIdDocumentsError,
  type PutApiShipmentsByIdError,
  type PutApiShipmentsByIdNextShipmentIntervalsData,
  type PutApiShipmentsByIdNextShipmentIntervalsError,
  type ShipmentDetailsDto,
  type ShipmentDto,
} from '@/api';

/*
 * Запись отгрузок. Каждая мутация возвращает карточку после изменения, а кеш
 * обновляется инвалидацией: списки и карточки перезапрашиваются. Оптимистичных
 * обновлений нет - сервер решает, разрешён ли переход (409), и показывать
 * несостоявшееся состояние нельзя.
 *
 * Ключи строит кодогенератор: [{ _id: 'getApiShipments', query }]. TanStack Query
 * сравнивает объекты ключа частично, поэтому [{ _id }] накрывает все варианты
 * фильтров и все карточки разом.
 */

/**
 * Ключ расчёта плана из истории. Расчёт - POST с телом, генератор строит для него
 * только мутацию, поэтому запрос (features/shipment-plan) собирает ключ сам, а
 * инвалидация живёт здесь, рядом с остальной.
 */
export const SHIPMENT_FORECAST_KEY = 'shipmentForecast';

const useInvalidateShipments = () => {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [{ _id: 'getApiShipments' }] }),
      queryClient.invalidateQueries({
        queryKey: [{ _id: 'getApiShipmentsById' }],
      }),
      // Анализ объёма считается по составу отгрузки и истории клиента.
      queryClient.invalidateQueries({
        queryKey: [{ _id: 'getApiShipmentsByIdVolumeAnalysis' }],
      }),
      // Сводка «Моих задач» считается по статусам, задержкам и оплатам.
      queryClient.invalidateQueries({
        queryKey: [{ _id: 'getApiShipmentsTaskSummary' }],
      }),
      // План из истории продолжает цепочку от отгрузок: после записи он устарел.
      queryClient.invalidateQueries({ queryKey: [SHIPMENT_FORECAST_KEY] }),
    ]);
  };
};

// Типы возврата заданы явно: без них TypeScript выводит ссылку внутрь
// сгенерированного пакета (TS2742).

export const useCreateShipment = (): UseMutationResult<
  ShipmentDetailsDto,
  PostApiShipmentsError,
  Options<PostApiShipmentsData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({ ...postApiShipmentsMutation(), onSuccess: invalidate });
};

export const useUpdateShipment = (): UseMutationResult<
  ShipmentDetailsDto,
  PutApiShipmentsByIdError,
  Options<PutApiShipmentsByIdData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...putApiShipmentsByIdMutation(),
    onSuccess: invalidate,
  });
};

export const useTakeShipmentIntoWork = (): UseMutationResult<
  ShipmentDetailsDto,
  PostApiShipmentsByIdTakeIntoWorkError,
  Options<PostApiShipmentsByIdTakeIntoWorkData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...postApiShipmentsByIdTakeIntoWorkMutation(),
    onSuccess: invalidate,
  });
};

/**
 * «Назад по этапу»: «Согласование» -> «Запланировано», пока заказа в 1С нет.
 * Отметки согласования снимаются; следующие отгрузки, созданные «Взять в
 * работу», остаются, и повторное «Взять в работу» их второй раз не создаёт.
 */
export const useReturnShipmentToPlan = (): UseMutationResult<
  ShipmentDetailsDto,
  PostApiShipmentsByIdReturnToPlanError,
  Options<PostApiShipmentsByIdReturnToPlanData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...postApiShipmentsByIdReturnToPlanMutation(),
    onSuccess: invalidate,
  });
};

/** «Передать в доставку»: «Подготовка» -> «Доставка»; трек-номер и дата отгрузки - необязательны. */
export const useSendShipmentToDelivery = (): UseMutationResult<
  ShipmentDetailsDto,
  PostApiShipmentsByIdSendToDeliveryError,
  Options<PostApiShipmentsByIdSendToDeliveryData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...postApiShipmentsByIdSendToDeliveryMutation(),
    onSuccess: invalidate,
  });
};

/** Данные доставки - трек-номер и дата отгрузки; на любом этапе, кроме «Отменено». */
export const useSetShipmentDelivery = (): UseMutationResult<
  ShipmentDetailsDto,
  PutApiShipmentsByIdDeliveryError,
  Options<PutApiShipmentsByIdDeliveryData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...putApiShipmentsByIdDeliveryMutation(),
    onSuccess: invalidate,
  });
};

/** «Назад по этапу»: «Доставка» -> «Подготовка», пока 1С не отгрузила заказ. */
export const useReturnShipmentToPreparation = (): UseMutationResult<
  ShipmentDetailsDto,
  PostApiShipmentsByIdReturnToPreparationError,
  Options<PostApiShipmentsByIdReturnToPreparationData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...postApiShipmentsByIdReturnToPreparationMutation(),
    onSuccess: invalidate,
  });
};

export const useCancelShipment = (): UseMutationResult<
  ShipmentDetailsDto,
  PostApiShipmentsByIdCancelError,
  Options<PostApiShipmentsByIdCancelData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...postApiShipmentsByIdCancelMutation(),
    onSuccess: invalidate,
  });
};

/**
 * «Сформировать заказ в 1С» и «Отправить снова» после отказа. Ответ - карточка «в
 * пути»; исход приходит позже, его приносит опрос карточки (useShipmentCard).
 */
export const useStartShipmentHandover = (): UseMutationResult<
  ShipmentDetailsDto,
  PostApiShipmentsByIdHandoverError,
  Options<PostApiShipmentsByIdHandoverData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...postApiShipmentsByIdHandoverMutation(),
    onSuccess: invalidate,
  });
};

/** Создание отгрузок, выбранных в плане из истории: одним сохранением, все или ни одной. */
export const useCreateShipmentsFromForecast = (): UseMutationResult<
  Array<ShipmentDto>,
  PostApiShipmentsFromForecastError,
  Options<PostApiShipmentsFromForecastData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...postApiShipmentsFromForecastMutation(),
    onSuccess: invalidate,
  });
};

/**
 * Интервал до следующей отгрузки по строкам - в любом статусе, кроме «Отменено»:
 * это данные планирования, а не поле заказа 1С.
 */
export const useSetNextShipmentIntervals = (): UseMutationResult<
  ShipmentDetailsDto,
  PutApiShipmentsByIdNextShipmentIntervalsError,
  Options<PutApiShipmentsByIdNextShipmentIntervalsData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...putApiShipmentsByIdNextShipmentIntervalsMutation(),
    onSuccess: invalidate,
  });
};

/**
 * Пометить задержанной или сменить причину. Не правка: работает на любом этапе,
 * кроме закрытых, и после передачи в 1С тоже.
 */
export const useMarkShipmentDelayed = (): UseMutationResult<
  ShipmentDetailsDto,
  PostApiShipmentsByIdDelayError,
  Options<PostApiShipmentsByIdDelayData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...postApiShipmentsByIdDelayMutation(),
    onSuccess: invalidate,
  });
};

/** Снять метку задержки. */
export const useClearShipmentDelay = (): UseMutationResult<
  ShipmentDetailsDto,
  DeleteApiShipmentsByIdDelayError,
  Options<DeleteApiShipmentsByIdDelayData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...deleteApiShipmentsByIdDelayMutation(),
    onSuccess: invalidate,
  });
};

/**
 * Отметить шаг согласования или снять отметку. Шаги идут по порядку, снятие
 * снимает и следующие; пока все три не отмечены, передача в 1С закрыта.
 */
export const useSetShipmentApprovalStep = (): UseMutationResult<
  ShipmentDetailsDto,
  PutApiShipmentsByIdApprovalStepsError,
  Options<PutApiShipmentsByIdApprovalStepsData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...putApiShipmentsByIdApprovalStepsMutation(),
    onSuccess: invalidate,
  });
};

/**
 * Отметить документ подготовленным или снять отметку. Чек-лист менеджера на
 * любом этапе, кроме закрытых; переходы он не блокирует.
 */
export const useSetShipmentDocumentMark = (): UseMutationResult<
  ShipmentDetailsDto,
  PutApiShipmentsByIdDocumentsError,
  Options<PutApiShipmentsByIdDocumentsData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...putApiShipmentsByIdDocumentsMutation(),
    onSuccess: invalidate,
  });
};

/** «Запросить исход» из «нет ответа от 1С»: та же команда уходит ещё раз. */
export const useRequestShipmentHandoverOutcome = (): UseMutationResult<
  ShipmentDetailsDto,
  PostApiShipmentsByIdHandoverRequestOutcomeError,
  Options<PostApiShipmentsByIdHandoverRequestOutcomeData>
> => {
  const invalidate = useInvalidateShipments();

  return useMutation({
    ...postApiShipmentsByIdHandoverRequestOutcomeMutation(),
    onSuccess: invalidate,
  });
};
